import {Injectable} from "@angular/core";
import {productFragments} from "../product-services/product.service";
import {VisbosService} from "../../../core/visbos-core/graphql/visbos.service";
import {Apollo, ApolloQueryObservable} from "apollo-angular";
import {NgRedux} from "@angular-redux/store";
import {IAppState} from "../../../shared/redux-store/store";
import {SessionActions} from "../../../shared/actions/session.actions";
import {Observable} from "rxjs/Observable";
import {ShoppingCart} from "../../../shared/view-model/store-management/shopping-cart.type";
import {supplierFragments} from "../../../crm/services/supplier/supplier.service";
import {adminUnitFragments} from "../../../organization/services/administration_unit_service/administration-unit.service";
import {Order} from "../../../shared/view-model/store-management/order.type";
import {ApolloQueryResult} from "apollo-client";
import gql from "graphql-tag";
import {
  FillUpDepotMutation,
  GenerateDispatchListPdfMutation,
  LoadOrderQueryQuery,
  PrefetchShoppingCartsQuery
} from "../../../shared/graphql/graphql.types";
import {warehousingFragments} from "../warehousing-service/warehousing.service";
import {storeManagementFragments} from "../withdrawal.service";
import {DispatchList} from "../../../shared/view-model/store-management/dispatch-list.type";
import {filter} from "graphql-anywhere";

export const procurementFragments = {
  shoppingCartItem: gql`
    fragment ShoppingCartItemFields on ShoppingCartItem {
      cuid,
      label,
      is_editable,
      article_number,
      article_label,
      packing_unit,
      packing_content,
      amount,
      price,
      total_price,
      edited,
      removed,
      referenced_stocks {
        cuid, label,
        min_stock, report_stock, shall_stock, act_stock,
        order_amount,
        depot { cuid, label }
      }
      stock_item { cuid, label }
      product_packing {
        ...ProductPackingFields
      }
    }

    ${productFragments.productPacking}
  `
};

export const fragments = {
  shoppingCart: gql`
    fragment ShoppingCartFields on ShoppingCart {
      supplier{cuid label is_administration_unit {cuid label}}
      cuid,
      label,
      is_editable,
      created_at,
      updated_at,
      total,
      shopping_cart_items{
        ...ShoppingCartItemFields
      }
    }

    ${procurementFragments.shoppingCartItem}
  `
};

fragments['orderItem'] = gql`
  fragment OrderItemFields on OrderItem {
    cuid, label, is_editable
    article_number, article_label
    packing_unit, packing_content,
    amount, amount_in_orig_packing_open,
    amount_in_pieces, amount_in_pieces_delivered, amount_in_pieces_open
    price, total_price
    referenced_stocks {
      cuid, label,
      min_stock, report_stock, shall_stock, act_stock,
      order_amount,
      depot { cuid, label }
      depot_yard { cuid, label }
      identities { cuid, label, ean, ean_type }
      registration_required
    }
    available_stocks {
      cuid, label,
      min_stock, report_stock, shall_stock, act_stock,
      order_amount,
      depot { cuid, label }
      depot_yard { cuid, label }
      identities { cuid, label, ean, ean_type }
      registration_required
    }
    stock_item {cuid, label}
    product_packing {cuid, label}
  }
`;

fragments['orderItem'] = gql`
  fragment DeliveryItemFields on DeliveryItem {
    cuid, label, is_editable
    article_number, article_label
    packing_unit, packing_content,
    amount, amount_in_pieces
    price, total_price
    referenced_order_item {
      ...OrderItemFields
    }
    stock_item {cuid, label}
    product_packing {cuid, label}
  }
  ${fragments['orderItem']}
`;

fragments['delivery'] = gql`
  fragment DeliveryFields on Delivery {
    cuid, label, is_editable
    created_at, updated_at
    delivery_date, recipient_name, packing_slip_number, commission_number
    total
    administration_unit {cuid, label}
    supplier {cuid, label}
    order {cuid, label}
    delivery_items {
      ...DeliveryItemFields
    }
  }
  ${fragments['orderItem']}
`;

fragments['order_overview'] = gql`
  fragment OrderOverviewFields on Order {
    cuid, label, is_editable
    created_at, updated_at, is_completed
    recipient_name, recipient_street, recipient_city, recipient_country
    billing_address_name, billing_address_street, billing_address_city, billing_address_country
    delivery_address_name, delivery_address_street, delivery_address_city, delivery_address_country
    order_date, customer_number, commission_number
    ordered_via, order_data
    contact_person, contact_data
    administration_unit {cuid, label}
    supplier {cuid, label}
  }
`;

fragments['order'] = gql`
  fragment OrderFields on Order {
    cuid, label, is_editable
    created_at, updated_at, is_completed
    recipient_name, recipient_street, recipient_city, recipient_country
    billing_address_name, billing_address_street, billing_address_city, billing_address_country
    delivery_address_name, delivery_address_street, delivery_address_city, delivery_address_country
    order_date, customer_number, commission_number
    ordered_via, order_data
    contact_person, contact_data
    total
    administration_unit {cuid, label}
    supplier {cuid, label}
    deliveries {
      ...DeliveryFields
    }
    order_items {
      ...OrderItemFields
    }
  }
  ${fragments['orderItem']}
  ${fragments['delivery']}
`;


export const loadShoppingCartQuery = gql`
  query LoadShoppingCart($AU: String, $SU: String, $cuid: String) {
    shoppingCart(administration_unit_cuid: $AU, supplier_cuid: $SU, cuid: $cuid) {
      ...ShoppingCartFields,
      administration_unit {
        ...AdminUnitDetailFields
      },
      supplier {
        ...SupplierFields
      }
    }
  }

  ${fragments.shoppingCart}
  ${supplierFragments.supplier}
  ${adminUnitFragments.adminUnit}
`;

export const loadShoppingCartsForAdminUnitQuery = gql`
  query LoadShoppingCartsForAdminUnit($AU: String!) {
    shoppingCarts(administration_unit_cuid: $AU) {
      ...ShoppingCartFields
    }
  }

  ${fragments.shoppingCart}
`;

export const prefetchShoppingCartsQuery = gql`
  query PrefetchShoppingCarts($adminUnitCuid: String!) {
    shoppingCartsOverview(administration_unit_cuid:$adminUnitCuid) {
      cuid label
      supplier {cuid label}
      no_of_items
      is_generated_temporarily
    }
  }
`;

export const autoFillShoppingCartMutation = gql`
  mutation AutoFillShoppingCarts($shoppingCartCuid: String!) {
    autoFillShoppingCart(shoppingCartCuid: $shoppingCartCuid) {
      ...ShoppingCartFields
    }
  }

  ${fragments.shoppingCart}
`;

export const createShoppingCartItemMutation = gql`
  mutation CreateShoppingCartItemMutation($sCartCuid: String, $stockCuid: String, $packingCuid: String, $data: ShoppingCartItemInput) {
    createShoppingCartItem(shopping_cart_cuid: $sCartCuid, stock_cuid: $stockCuid, product_packing_cuid: $packingCuid, shopping_cart_item_input: $data) {
      ...ShoppingCartItemFields
    }
  }
  ${procurementFragments.shoppingCartItem}
`;

export const createShoppingCartItemsMutation = gql`
  mutation CreateShoppingCartItemsMutation($shoppingCartCuid: String, $itemInput: [ShoppingCartItemInput]!) {
    createShoppingCartItems(shopping_cart_cuid: $shoppingCartCuid, shopping_cart_item_input: $itemInput) {
      ...ShoppingCartItemFields
    }
  }
  ${procurementFragments.shoppingCartItem}
`;

export const updateShoppingCartItemMutation = gql`
  mutation UpdateShoppingCartItemMutation($cuid: String!, $data: ShoppingCartItemInput!) {
    updateShoppingCartItem(shopping_cart_item_cuid: $cuid, shopping_cart_item_input: $data) {
      ...ShoppingCartItemFields
    }
  }
  ${procurementFragments.shoppingCartItem}
`;

export const removeShoppingCartItemMutation = gql`
  mutation RemoveShoppingCartItemMutation($cuid: String!) {
    removeShoppingCartItem(shopping_cart_item_cuid: $cuid)
  }
`;

export const resetShoppingCartsMutation = gql`
  mutation ResetShoppingCarts($adminUnitCuid: String!, $shoppingCartCuids: [String]) {
    resetShoppingCarts(administration_unit_id: $adminUnitCuid, shopping_cart_cuids: $shoppingCartCuids) {
      ...ShoppingCartFields
    }
  }
  ${fragments.shoppingCart}
`;

export const placeNewOrderMutation = gql`
  mutation PlaceNewOrder($cartCuid: String!, $order: OrderInput!) {
    placeOrder(shopping_cart_cuid: $cartCuid, order_input: $order) {
      ...OrderFields
    }
  }
  ${fragments['order']}
`;

export const updateOrderStatusMutation = gql`
  mutation UpdateOrder($orderCuid: String!, $order: OrderInput!) {
    updateOrder(orderCuid: $orderCuid, orderInput: $order) {
      cuid created_at updated_at
      is_completed
    }
  }
`;

export const loadOrderQuery = gql`
  query LoadOrderQuery($orderCuid: String!) {
    order(cuid: $orderCuid) {
      ...OrderFields
    }
  }
  ${fragments['order']}
`;

export const loadOrderForDeliveryQuery = gql`
  query LoadOrderForDeliveryQuery($orderCuid: String!) {
    order(cuid: $orderCuid) {
      ...OrderFields
    }
  }
  ${fragments['order']}
`;

export const fetchOrdersQuery = gql`
  query FetchOrdersQuery($adminUnitCuid: String!, $onlyOpen: Boolean, $supplierCuid:String, $year: Int) {
    orders(adminUnitCuid: $adminUnitCuid, onlyOpenOrders: $onlyOpen, supplierCuid: $supplierCuid, year: $year) {
      ...OrderOverviewFields
    }
  }
  ${fragments['order_overview']}
`;

export const createDeliveryMutation = gql`
  mutation CreateDelivery($orderCuid: String!, $deliveryInput: DeliveryInput!, $medicineAuthData: MedicineStockChangeInput) {
    createDelivery(order_cuid: $orderCuid, delivery_input: $deliveryInput, medicineAuthData: $medicineAuthData) {
      ...DeliveryFields
    }
  }
  ${fragments['delivery']}
`;

export const getDispatchListForDepot = gql`
  query GetDispatchListForDepot($targetDepotCuid:String!, $sourceAdminUnitCuid: String!, $sourceDepotsCuids: [String]) {
    dispatchListForDepot(targetDepotCuid: $targetDepotCuid, sourceDepotsCuids:$sourceDepotsCuids, sourceAdminUnitCuid: $sourceAdminUnitCuid) {
      cuid, label, created_at
      dispatch_list_items {
        cuid, label
        stock_item {
          cuid label
          products {
            cuid label
            packings {
              cuid label
              packing_unit {cuid label}
            }
          }
        }
        amount packing_unit packing_unit_content
        product_packing {cuid label packing_amount}
        source_stocks {
          cuid label
          min_stock report_stock shall_stock act_stock
          depot {
            cuid label
            administration_unit { cuid label }
          }
          depot_yard { cuid label }
          registration_required
        }
        target_stocks {
          cuid label
          min_stock report_stock shall_stock act_stock
          depot {
            cuid label
            administration_unit { cuid label }
          }
          depot_yard { cuid label }
          registration_required
        }
      }
    }
  }
`;

export const generateDispatchListPdf = gql`
  mutation GenerateDispatchListPdf($dispatchListData: DispatchListInput!) {
    dispatchListPdf(dispatchList: $dispatchListData)
  }
`;

export const fillUpDepotMutation = gql`
  mutation FillUpDepot($fillUpDepotInput: FillUpDepotInput!, $authData: MedicineStockChangeInput, $generateVouchers: Boolean) {
    fillUpDepot(fillUpData: $fillUpDepotInput, authData:$authData, generateVouchers: $generateVouchers) {
      withdrawal {
        ...WithdrawalFields
      }
      warehousing {
        ...WarehousingFields
      }
      voucher_withdrawal,
      voucher_warehousing
    }
  }
  ${warehousingFragments.warehousing}
  ${storeManagementFragments.withdrawal}
`;

export const DeleteOrderMutation = gql`
  mutation DeleteOrder($cuid: String!) {
    deleteOrder(cuid: $cuid)
  }
`;

export interface ShoppingCartResult {
  shoppingCart: ShoppingCart;
}

export interface ShoppingCartListResult {
  shoppingCarts: ShoppingCart[];
}

export interface OrderResult {
  order: Order
}

export interface OrdersResult {
  orders: Order[]
}

export const DispatchListInputFragment = gql`
  fragment DispatchListInputFragment on DispatchList {
    user {cuid}
    dispatch_list_items {
      cuid, amount,
      stock_item {cuid}
      target_stocks {cuid}
      source_stocks {cuid}
    }
  }
`;

@Injectable()
export class ProcurementService extends VisbosService {

  constructor(apollo: Apollo, ngRedux: NgRedux<IAppState>, sessionActions: SessionActions) {
    super(apollo, ngRedux, sessionActions);
  }

  public loadShoppingCart(supplierCuid?: Observable<string> | string, adminUnitCuid?: Observable<string>
    | string, shoppingCartCuid?: string
    | Observable<string>): ApolloQueryObservable<ShoppingCartResult> {

    return this.fetchItems<ShoppingCartResult>({
      query: loadShoppingCartQuery,
      variables: {
        cuid: shoppingCartCuid,
        AU: adminUnitCuid || this.activeAdminUnitCuid$,
        SU: supplierCuid
      }
    });

  }

  public prefetchShoppingCarts(adminUnitCuid?): ApolloQueryObservable<PrefetchShoppingCartsQuery> {
    return this.fetchItems({
      query: prefetchShoppingCartsQuery,
      variables: {
        adminUnitCuid: adminUnitCuid || this.activeAdminUnitCuid$
      }
    })
  }

  public loadShoppingCarts(adminUnitCuid?: Observable<string> | string): ApolloQueryObservable<ShoppingCartListResult> {

    return this.fetchItems<ShoppingCartListResult>({
      query: loadShoppingCartsForAdminUnitQuery,
      variables: {
        AU: adminUnitCuid || this.activeAdminUnitCuid$
      }
    });

  }

  public createShoppingCartItems(shoppingCartCuid: string, cartItemData: any[]) {
    return this.mutate({
      mutation: createShoppingCartItemsMutation,
      variables: {
        shoppingCartCuid: shoppingCartCuid,
        itemInput: cartItemData
      },
      refetchQueries: ['LoadShoppingCart', 'LoadShoppingCartsForAdminUnit', 'FetchStocksForDepotQuery', 'LoadShoppingCartsForSubAdminUnitQuery']
    });
  }

  public createShoppingCartItem(shoppingCartCuid?: string, stockCuid?: string, productPackingCuid?: string, cartItemData?: any) {
    return this.mutate({
      mutation: createShoppingCartItemMutation,
      variables: {
        sCartCuid: shoppingCartCuid,
        stockCuid: stockCuid,
        packingCuid: productPackingCuid,
        data: cartItemData
      },
      updateQueries: {
        FetchStocksForDepotQuery: (prev, {mutationResult}) => {
          if (!mutationResult.data) {
            return prev;
          }
          const newStockCartItem = mutationResult.data.createShoppingCartItem;

          const newResult = {
            fetchStocks: prev.fetchStocks.pipe(st => {
              if (newStockCartItem.referenced_stocks.find(refSt => refSt.cuid == st.cuid)) {
                return Object.assign(st, {
                  in_shopping_cart: true
                });
              } else {
                return st;
              }
            })
          };

          return newResult;

        }
      },
      refetchQueries: ['LoadShoppingCart', 'LoadShoppingCartsForAdminUnit', 'FetchStocksForDepotQuery', 'LoadShoppingCartsForSubAdminUnitQuery']
    })
  }

  public updateShoppingCartItem(cuid: string, data, visible = true) {
    return this.mutate({
      mutation: updateShoppingCartItemMutation,
      variables: {
        cuid: cuid,
        data: data
      },
      refetchQueries: ['LoadShoppingCart', 'LoadShoppingCartsForAdminUnit', 'LoadShoppingCartsForSubAdminUnitQuery']
    }, visible);
  }

  public removeShoppingCartItem(cuid: string, visible = true) {
    return this.mutate({
      mutation: removeShoppingCartItemMutation,
      variables: {
        cuid: cuid
      },
      refetchQueries: ['LoadShoppingCart', 'LoadShoppingCartsForAdminUnit', 'FetchStocksForDepotQuery', 'LoadShoppingCartsForSubAdminUnitQuery']
    }, visible);
  }

  public resetShoppingCart(adminUnitCuid?: string, shoppingCartCuids?: string[], visible = true) {
    return this.mutate({
      mutation: resetShoppingCartsMutation,
      variables: {
        adminUnitCuid: adminUnitCuid || this.ngRedux.getState().session.activeAdminUnit,
        shoppingCartCuids: shoppingCartCuids
      },
      refetchQueries: ['LoadShoppingCart', 'LoadShoppingCartsForAdminUnit', 'FetchStocksForDepotQuery', 'LoadShoppingCartsForSubAdminUnitQuery']
    }, visible);
  }

  public placeNewOrder(cartCuid: string, orderData: any, visible = true) {
    return this.mutate({
      mutation: placeNewOrderMutation,
      variables: {
        cartCuid: cartCuid,
        order: orderData
      },
      refetchQueries: ['FetchOrdersQuery', 'LoadShoppingCartsForAdminUnit', 'FetchStocksForDepotQuery', 'LoadShoppingCartsForSubAdminUnitQuery']
    }, visible);
  }

  public updateOrderStatus(orderCuid: string, orderData: any) {
    return this.mutate({
      mutation: updateOrderStatusMutation,
      variables: {
        orderCuid: orderCuid,
        order: orderData
      },
      refetchQueries: ['FetchOrdersQuery']
    });
  }

  public generateOrderPDF(orderCuid: string | Observable<string>): Observable<ApolloQueryResult<{ generateOrderPDF: string }>> {
    return this.fetchItems<{ generateOrderPDF: string }>({
      query: gql`
        query GenerateOrderPDF($orderCuid: String!) {
          generateOrderPDF(order_cuid: $orderCuid)
        }
      `,
      variables: {
        orderCuid: orderCuid
      },
      fetchPolicy: 'cache-first'
    });
  }

  public loadOrder(orderCuid: string | Observable<string>): ApolloQueryObservable<LoadOrderQueryQuery> {
    return this.fetchItems({
      query: loadOrderQuery,
      variables: {
        orderCuid: orderCuid
      }
    });
  }

  public prefetchOrder(orderCuid: string | Observable<string>) {
    return this.prefetchItems({
      query: loadOrderQuery,
      variables: {
        orderCuid: orderCuid
      }
    });
  }

  public fetchOrders(adminUnitCuid?: string | Observable<string>,
                     onlyOpen: boolean | Observable<boolean> = false,
                     supplierCuid?: string
                       | Observable<string>, year?: number): ApolloQueryObservable<OrdersResult> {
    return this.fetchItems<OrdersResult>({
      query: fetchOrdersQuery,
      variables: {
        adminUnitCuid: adminUnitCuid || this.activeAdminUnitCuid$,
        onlyOpen: onlyOpen,
        supplierCuid: supplierCuid,
        year: year
      }
    })
  }

  public deleteOrder(cuid: string) {
    return this.mutate({
      mutation: DeleteOrderMutation,
      variables: {
        cuid: cuid
      },
      refetchQueries: ['FetchOrdersQuery', 'LoadOrderQuery', 'DepotsWithCriticalStocks', 'FetchStocksForDepotQuery', 'FetchCriticalStocksQuery']
    });
  }

  public getDispatchListForDepot(targetDepotCuid: string | Observable<string>,
                                 sourceAdminUnitCuid?: string | Observable<string>,
                                 sourceDepotsCuids?: string[]
                                   | Observable<string[]>): ApolloQueryObservable<any> {

    return this.fetchItems<any>({
      query: getDispatchListForDepot,
      variables: {
        targetDepotCuid: targetDepotCuid,
        sourceAdminUnitCuid: sourceAdminUnitCuid || this.activeAdminUnitCuid$,
        sourceDepotsCuids: sourceDepotsCuids
      }
    });

  }

  public fillUpDepot(fillUpDepotInput: any, authData?: any, generateVouchers: boolean = false, visible = true) {
    return this.mutate<FillUpDepotMutation>({
      mutation: fillUpDepotMutation,
      variables: {
        fillUpDepotInput: fillUpDepotInput,
        authData: authData,
        generateVouchers: generateVouchers
      },
      refetchQueries: ['DepotsWithCriticalStocks', 'FetchStocksForDepotQuery', 'FetchCriticalStocksQuery']
    }, visible);
  }

  public generateDispatchListPDF(dispatchList: DispatchList): Observable<ApolloQueryResult<GenerateDispatchListPdfMutation>> {

    const clearedData = filter(DispatchListInputFragment, dispatchList);

    return this.mutate({
      mutation: generateDispatchListPdf,
      variables: {
        dispatchListData: clearedData
      }
    })
  }

  public createDelivery(orderCuid: string | Observable<string>, deliveryInput: any, medicineAuthData: any, visible = true) {
    return this.mutate({
      mutation: createDeliveryMutation,
      variables: {
        orderCuid: orderCuid,
        deliveryInput: deliveryInput,
        medicineAuthData: medicineAuthData
      },
      refetchQueries: ['FetchOrdersQuery', 'LoadOrderQuery', 'DepotsWithCriticalStocks', 'FetchStocksForDepotQuery', 'FetchCriticalStocksQuery']
    }, visible);
  }


}



// WEBPACK FOOTER //
// ./src/app/store-management/services/procurement-service/procurement.service.ts